import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { PricingGroup } from '../usePricingTable'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { PricingTableCompactPricingRow } from './PricingTableCompactRows'
import { useProductReordering } from 'Cube/domain'
import { useMemo, useState } from 'react'
import { sortProductsByPresentationOrder } from '@sequencehq/quote-content'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

export const PricingTableCompactGroup = ({
  phaseId,
  group
}: {
  phaseId: string
  group: PricingGroup
}) => {
  const { productOrder, setProductOrder, enabled } = useProductReordering({
    phaseId
  })
  const [itemBeingDragged, setItemBeingDragged] =
    useState<UniqueIdentifier | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const orderedProductsInGroup = useMemo(
    () =>
      sortProductsByPresentationOrder({
        orderedProductIds: productOrder,
        products: group.pricingRows
      }),
    [group.pricingRows, productOrder]
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => {
        setItemBeingDragged(active.id)
      }}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          /**
           * The id of the rows is based on the price/discount id,
           * so we need to find the productIds if we're to determine
           * order.
           */
          const activeItem = orderedProductsInGroup.find(
            item => item.id === active.id
          )
          const overItem = orderedProductsInGroup.find(
            item => item.id === over.id
          )

          const activeIndex = productOrder.findIndex(
            id => id === activeItem?.productId
          )
          const overIndex = productOrder.findIndex(
            id => id === overItem?.productId
          )

          setProductOrder(arrayMove(productOrder, activeIndex, overIndex))
        }
        setItemBeingDragged(null)
      }}
    >
      <SortableContext
        items={orderedProductsInGroup}
        strategy={verticalListSortingStrategy}
      >
        {orderedProductsInGroup.map(row => (
          <PricingTableCompactPricingRow
            key={row.id}
            dragDisabled={
              !enabled ||
              Boolean(itemBeingDragged && itemBeingDragged !== row.id)
            }
            pricingRow={row}
            hasMinimum={Boolean(group.minimumRow)}
            isDragging={itemBeingDragged === row.id}
          />
        ))}
      </SortableContext>
    </DndContext>
  )
}
